import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Mail, CheckCircle, Loader, ArrowRight, AlertCircle } from "lucide-react";
import { subscribeToNewsletter } from "./newsletterService";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5087";

export default function TrackProject() {
  const [email, setEmail] = useState("");
  const [projectData, setProjectData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSubmit = async () => {
    if (!email.trim()) return;

    setLoading(true);
    setError("");
    setProjectData(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/projects/${encodeURIComponent(email.trim())}`,
      );

      if (response.status === 404) {
        setError("No project found for this email.");
        return;
      }

      if (!response.ok) {
        throw new Error("Server error");
      }

      const data = await response.json();
      setProjectData(data);
    } catch (err) {
      console.error(err);
      setError("Failed to connect to server.");
    } finally {
      setLoading(false);
    }
  };

  const STEP_NAMES = ["", "Discovery", "Design", "Development", "Testing", "Launch", "Completed"];

  const getStepName = (step) =>
    typeof step === "number" ? (STEP_NAMES[step] || "Unknown") : (step || "Unknown");

  const getStatusColor = (step) => {
    const name = getStepName(step);
    const colorMap = {
      "Discovery": "#6366f1",
      "Design": "#f97316",
      "Development": "#0ea5e9",
      "Testing": "#10b981",
      "Launch": "#8b5cf6",
      "Completed": "#059669",
    };
    return colorMap[name] || "#64748b";
  };

  const startProject = () => {
    window.location.href = "/#contact";
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const email = form.newsletterEmail?.value || "";
    const result = subscribeToNewsletter(email);
    window.alert(result.message);
    if (result.ok) {
      form.reset();
    }
  };

  return (
    <div className="track-project-page">
      {/* Navigation Bar */}
      <nav className={`navbar ${isScrolled ? "scrolled" : ""}`}>
        <div className="container navbar-container">
          <a href="/" className="navbar-brand">
            <img src="/TechXM-logo.png" alt="TECHXM logo" className="brand-logo-mark" width="30" height="30" />
          </a>

          <div className={`navbar-menu ${mobileMenuOpen ? "active" : ""}`}>
            <div className="services-dropdown">
              <div className="navbar-link">Services</div>
              <div className="dropdown-menu">
                <Link to="/web-development" className="dropdown-item">
                  <i className="fas fa-laptop-code"></i>
                  Web Development Services
                </Link>
                <Link to="/mobile-app" className="dropdown-item">
                  <i className="fas fa-mobile-alt"></i>
                  Mobile App Development
                </Link>
                <Link to="/custom-software" className="dropdown-item">
                  <i className="fas fa-cogs"></i>
                  Custom Software Development
                </Link>
                <Link to="/api-backend" className="dropdown-item">
                  <i className="fas fa-plug"></i>
                  API & Backend Services
                </Link>
                <Link to="/maintenance" className="dropdown-item">
                  <i className="fas fa-tools"></i>
                  Website & App Maintenance
                </Link>
                <Link to="/ai-services" className="dropdown-item">
                  <i className="fas fa-robot"></i>
                  AI & Machine Learning Services
                </Link>
              </div>
            </div>
            <a href="/#about" className="navbar-link">
              About
            </a>
            <a href="/#faq" className="navbar-link">
              FAQ
            </a>
            <a href="/#contact" className="navbar-link">
              Contact
            </a>
            <Link to="/track-project" className="navbar-link active-link">
              Track Project
            </Link>
            <button className="btn btn-primary nav-consult-btn" onClick={startProject}>
              Request Consultation
            </button>
          </div>

          <button
            className="navbar-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={`fas ${mobileMenuOpen ? "fa-times" : "fa-bars"}`}></i>
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <div className="breadcrumb">
            <Link to="/">Home</Link> / <span className="current">Track Project</span>
          </div>
          <h1 className="page-title">Track Your Project</h1>
          <p className="hero-description">
            Enter your email address below to get real-time updates on your project's status.
            We keep you informed at every stage of development.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section">
        <div className="container">

          {/* Search Card */}
          <div className="lookup-card">
            <div className="lookup-inner">
              <p className="lookup-label">Enter your email to get started</p>

              <div className={`lookup-input-wrap ${loading ? "loading" : ""}`}>
                <Mail size={18} className="lookup-icon" />
                <input
                  type="email"
                  placeholder="yourname@email.com"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(""); setProjectData(null); }}
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                  disabled={loading}
                  className="lookup-input"
                />
                {email && !loading && (
                  <button
                    onClick={() => { setEmail(""); setError(""); setProjectData(null); }}
                    className="lookup-clear"
                    aria-label="Clear"
                  >
                    ×
                  </button>
                )}
              </div>

              <button
                onClick={handleSubmit}
                disabled={loading || !email.trim()}
                className="lookup-btn"
              >
                {loading ? (
                  <>
                    <Loader size={16} className="spin" />
                    Checking…
                  </>
                ) : (
                  <>
                    Check Status
                    <ArrowRight size={16} />
                  </>
                )}
              </button>

              {error && (
                <div className="lookup-error">
                  <AlertCircle size={16} />
                  <span>{error}</span>
                </div>
              )}
            </div>
          </div>

          {/* Result Card */}
          {projectData && (
            <div className="result-card">
              <div className="result-card-top">
                <div className="result-found-row">
                  <CheckCircle size={20} className="result-check" />
                  <span className="result-found-text">Project found</span>
                  <span
                    className="status-pill"
                    style={{ background: getStatusColor(projectData.currentStep) }}
                  >
                    {getStepName(projectData.currentStep)}
                  </span>
                </div>
                <h3 className="result-project-name">{projectData.projectName}</h3>
                <div className="result-meta">
                  <span>{projectData.clientName}</span>
                  <span className="meta-dot">·</span>
                  <span>{projectData.email}</span>
                </div>
              </div>

              <div className="result-card-body">
                <div className="progress-block">
                  <div className="progress-labels">
                    <span>Overall Progress</span>
                    <span className="progress-pct">{projectData.progress}%</span>
                  </div>
                  <div className="progress-track">
                    <div
                      className="progress-fill"
                      style={{ width: `${projectData.progress}%`, background: getStatusColor(projectData.currentStep) }}
                    />
                  </div>
                </div>

                <div className="steps-row">
                  {["Discovery", "Design", "Development", "Testing", "Launch"].map((step, idx) => {
                    const stepIndex = idx + 1;
                    const current = typeof projectData.currentStep === "number"
                      ? projectData.currentStep
                      : ({ Discovery: 1, Design: 2, Development: 3, Testing: 4, Launch: 5, Completed: 5 }[projectData.currentStep] || 0);
                    const done = stepIndex < current;
                    const active = stepIndex === current;
                    return (
                      <div key={step} className={`step-item ${done ? "done" : ""} ${active ? "active" : ""}`}>
                        <div className="step-dot">
                          {done ? <CheckCircle size={12} /> : stepIndex}
                        </div>
                        <span className="step-label">{step}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <p className="help-note">
            Need help? <Link to="/#contact">Contact our team</Link> and we'll look up your project.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-decoration"></div>
        <div className="footer-decoration"></div>

        <div className="container">
          <div className="footer-grid">
            <div>
              <div className="footer-brand">
                <img src="/TechXM-logo.png" alt="TECHXM logo" className="brand-logo-mark" width="30" height="30" />
              </div>
              <p className="footer-about">
                We design, build, and support secure digital systems for
                organizations that require reliability, scalability, and strong
                delivery governance.
              </p>
              <div className="footer-social">
                <a
                  href="https://www.linkedin.com/company/techxm/"
                  className="social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fab fa-linkedin"></i>
                </a>
                <a
                  href="https://www.instagram.com/techxm_za/"
                  className="social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fab fa-instagram"></i>
                </a>
                <a
                  href="https://www.facebook.com/"
                  className="social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fab fa-facebook"></i>
                </a>
              </div>
            </div>

            <div>
              <h4 className="footer-heading">Services</h4>
              <ul className="footer-links">
                <li>
                  <a href="/web-development">
                    <i className="fas fa-chevron-right"></i>
                    Web Development
                  </a>
                </li>
                <li>
                  <a href="/mobile-app">
                    <i className="fas fa-chevron-right"></i>
                    Mobile Apps
                  </a>
                </li>
                <li>
                  <a href="/custom-software">
                    <i className="fas fa-chevron-right"></i>
                    Custom Software
                  </a>
                </li>
                <li>
                  <a href="/api-backend">
                    <i className="fas fa-chevron-right"></i>
                    API & Backend
                  </a>
                </li>
                <li>
                  <a href="/maintenance">
                    <i className="fas fa-chevron-right"></i>
                    Maintenance
                  </a>
                </li>
                <li>
                  <a href="/ai-services">
                    <i className="fas fa-chevron-right"></i>
                    AI & Machine Learning
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="footer-heading">Company</h4>
              <ul className="footer-links">
                <li>
                  <a href="/#about">
                    <i className="fas fa-chevron-right"></i>
                    About
                  </a>
                </li>
                <li>
                  <a href="/#faq">
                    <i className="fas fa-chevron-right"></i>
                    FAQ
                  </a>
                </li>
                <li>
                  <a href="/track-project">
                    <i className="fas fa-chevron-right"></i>
                    Track Project
                  </a>
                </li>
                <li>
                  <a href="/#contact">
                    <i className="fas fa-chevron-right"></i>
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="footer-heading">Contact Info</h4>
              <div className="footer-contact">
                <div className="contact-item">
                  <i className="fas fa-map-marker-alt"></i>
                  <span>Ohenimuri, Walkerville</span>
                </div>
                <div className="contact-item">
                  <i className="fas fa-phone"></i>
                  <a href="tel:0765536048">076 553 6048</a>
                  <span> / </span>
                  <a href="tel:0742686212">074 268 6212</a>
                </div>
                <div className="contact-item">
                  <i className="fas fa-envelope"></i>
                  <a href="mailto:info@techxm.co.za">info@techxm.co.za</a>
                </div>
                <div className="contact-item">
                  <i className="fas fa-clock"></i>
                  <span>Mon - Fri: 9:00 - 18:00</span>
                </div>
              </div>

              <div className="footer-newsletter">
                <p className="newsletter-text">Subscribe to our newsletter</p>
                <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
                  <input
                    type="email"
                    name="newsletterEmail"
                    className="newsletter-input"
                    placeholder="Your email address"
                    required
                  />
                  <button type="submit" className="newsletter-btn">
                    <i className="fas fa-paper-plane"></i>
                  </button>
                </form>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>
              &copy; 2025 TECHXM. All rights reserved. Enterprise Digital
              Solutions.
            </p>
            <div className="footer-legal">
              <a href="/privacy-policy">Privacy Policy</a>
              <a href="/terms-of-service">Terms of Service</a>
              <a href="/cookie-policy">Cookie Policy</a>
            </div>
          </div>
        </div>
      </footer>

      <style jsx>{`
        .track-project-page {
          font-family: var(--font-body, "Manrope", "Segoe UI", system-ui, sans-serif);
          color: var(--text, #1e293b);
          background: #f8fafc;
          min-height: 100vh;
        }

        /* Navigation Bar */
        .navbar {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          z-index: 1000;
          transition: all 0.3s ease;
          padding: 1rem 0;
          border-bottom: 1px solid rgba(0, 0, 0, 0.1);
        }

        .navbar.scrolled {
          background: rgba(255, 255, 255, 0.98);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          padding: 0.75rem 0;
        }

        .navbar-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0 1.5rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .navbar-brand {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 700;
          font-size: 1.5rem;
          color: var(--primary, #0ea5e9);
          text-decoration: none;
        }

        .navbar-brand .brand-logo-mark {
          width: 150px;
          height: 90px;
          display: block;
          object-fit: contain;
        }

        .navbar-menu {
          display: flex;
          align-items: center;
          gap: 2rem;
        }

        .navbar-link {
          color: var(--text, #1e293b);
          text-decoration: none;
          font-weight: 500;
          transition: color 0.3s ease;
          cursor: pointer;
        }

        .navbar-link:hover {
          color: var(--primary, #0ea5e9);
        }

        .navbar-link.active-link {
          color: var(--primary, #008080);
          font-weight: 600;
        }

        .navbar-toggle {
          display: none;
          background: none;
          border: none;
          font-size: 1.5rem;
          color: var(--text, #1e293b);
          cursor: pointer;
        }

        /* Services Dropdown */
        .services-dropdown {
          position: relative;
        }

        .dropdown-menu {
          position: absolute;
          top: 100%;
          left: -2rem;
          background: white;
          border-radius: 8px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          padding: 1rem 0;
          min-width: 250px;
          opacity: 0;
          visibility: hidden;
          transform: translateY(10px);
          transition: all 0.3s ease;
          z-index: 100;
        }

        .services-dropdown:hover .dropdown-menu {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .dropdown-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1.5rem;
          color: var(--text, #1e293b);
          text-decoration: none;
          transition: background 0.3s ease;
        }

        .dropdown-item:hover {
          background: rgba(0, 128, 128, 0.1);
          color: var(--primary, #0ea5e9);
        }

        /* Hero Section */
        .page-hero {
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: white;
          padding: 8rem 0 5rem;
          position: relative;
          overflow: hidden;
          margin-top: 60px;
        }

        .page-hero::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.15) 0%, transparent 50%),
                      radial-gradient(circle at 80% 20%, rgba(99, 102, 241, 0.1) 0%, transparent 50%);
        }

        .breadcrumb {
          font-size: 0.9rem;
          margin-bottom: 2rem;
          color: rgba(255, 255, 255, 0.7);
          position: relative;
          z-index: 2;
        }

        .breadcrumb a {
          color: rgba(255, 255, 255, 0.7);
          text-decoration: none;
          transition: color 0.3s ease;
        }

        .breadcrumb a:hover {
          color: white;
        }

        .breadcrumb .current {
          color: white;
          font-weight: 600;
        }

        .page-title {
          font-size: 3rem;
          font-weight: 700;
          margin-bottom: 1rem;
          letter-spacing: -0.02em;
          position: relative;
          z-index: 2;
        }

        .hero-description {
          font-size: 1.1rem;
          line-height: 1.6;
          color: rgba(255, 255, 255, 0.85);
          max-width: 600px;
          position: relative;
          z-index: 2;
        }

        /* Container */
        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }

        .section {
          padding: 4rem 0;
        }

        /* ── Lookup Card ── */
        .lookup-card {
          max-width: 560px;
          margin: 0 auto;
          background: #fff;
          border-radius: 20px;
          box-shadow: 0 4px 24px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04);
          border: 1px solid #e8edf3;
          padding: 2.5rem 2rem 2rem;
        }

        .lookup-inner {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .lookup-label {
          font-size: 0.82rem;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          margin: 0;
        }

        .lookup-input-wrap {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          background: #f8fafc;
          border: 1.5px solid #e2e8f0;
          border-radius: 12px;
          padding: 0 1rem;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .lookup-input-wrap:focus-within {
          border-color: #008080;
          box-shadow: 0 0 0 3px rgba(0,128,128,0.08);
          background: #fff;
        }

        .lookup-input-wrap.loading {
          opacity: 0.7;
        }

        .lookup-icon {
          color: #94a3b8;
          flex-shrink: 0;
        }

        .lookup-input {
          flex: 1;
          border: none;
          background: transparent;
          padding: 0.9rem 0;
          font-size: 1rem;
          font-family: inherit;
          color: #0f172a;
          outline: none;
        }

        .lookup-input::placeholder {
          color: #b0bac5;
        }

        .lookup-input:disabled {
          cursor: not-allowed;
        }

        .lookup-clear {
          background: none;
          border: none;
          color: #94a3b8;
          font-size: 1.2rem;
          cursor: pointer;
          line-height: 1;
          padding: 0 2px;
          transition: color 0.15s;
        }

        .lookup-clear:hover {
          color: #475569;
        }

        .lookup-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          width: 100%;
          padding: 0.875rem 1.5rem;
          background: linear-gradient(135deg, #008080, #006666);
          color: #fff;
          font-size: 0.95rem;
          font-weight: 600;
          font-family: inherit;
          border: none;
          border-radius: 12px;
          cursor: pointer;
          transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s;
        }

        .lookup-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 8px 20px -6px rgba(0,128,128,0.45);
        }

        .lookup-btn:disabled {
          opacity: 0.55;
          cursor: not-allowed;
          transform: none;
        }

        .lookup-error {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          background: #fff5f5;
          border: 1px solid #fed7d7;
          border-radius: 10px;
          padding: 0.75rem 1rem;
          color: #c53030;
          font-size: 0.875rem;
          font-weight: 500;
        }

        /* ── Result Card ── */
        .result-card {
          max-width: 560px;
          margin: 1.5rem auto 0;
          background: #fff;
          border-radius: 20px;
          box-shadow: 0 4px 24px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04);
          border: 1px solid #e8edf3;
          overflow: hidden;
          animation: fadeUp 0.35s ease;
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .result-card-top {
          padding: 1.5rem 1.75rem 1.25rem;
          border-bottom: 1px solid #f1f5f9;
        }

        .result-found-row {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.75rem;
        }

        .result-check { color: #10b981; }

        .result-found-text {
          font-size: 0.8rem;
          font-weight: 600;
          color: #10b981;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .status-pill {
          margin-left: auto;
          padding: 0.2rem 0.75rem;
          border-radius: 20px;
          font-size: 0.72rem;
          font-weight: 700;
          color: #fff;
          letter-spacing: 0.3px;
          text-transform: uppercase;
        }

        .result-project-name {
          font-size: 1.2rem;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 0.35rem;
        }

        .result-meta {
          font-size: 0.85rem;
          color: #64748b;
          display: flex;
          align-items: center;
          gap: 0.4rem;
        }

        .meta-dot { color: #cbd5e1; }

        .result-card-body {
          padding: 1.25rem 1.75rem 1.5rem;
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        /* Progress */
        .progress-block {}

        .progress-labels {
          display: flex;
          justify-content: space-between;
          font-size: 0.82rem;
          font-weight: 500;
          color: #475569;
          margin-bottom: 0.5rem;
        }

        .progress-pct {
          font-weight: 700;
          color: #0f172a;
        }

        .progress-track {
          height: 8px;
          background: #e2e8f0;
          border-radius: 99px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          border-radius: 99px;
          transition: width 0.6s ease;
        }

        /* Steps */
        .steps-row {
          display: flex;
          justify-content: space-between;
          position: relative;
        }

        .steps-row::before {
          content: "";
          position: absolute;
          top: 10px;
          left: 10px;
          right: 10px;
          height: 2px;
          background: #e2e8f0;
          z-index: 0;
        }

        .step-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.4rem;
          flex: 1;
          position: relative;
          z-index: 1;
        }

        .step-dot {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          border: 2px solid #cbd5e1;
          background: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.65rem;
          font-weight: 700;
          color: #94a3b8;
          transition: all 0.2s;
        }

        .step-item.done .step-dot {
          background: #10b981;
          border-color: #10b981;
          color: #fff;
        }

        .step-item.active .step-dot {
          border-color: #008080;
          background: #008080;
          color: #fff;
          box-shadow: 0 0 0 3px rgba(0,128,128,0.15);
        }

        .step-label {
          font-size: 0.65rem;
          font-weight: 500;
          color: #94a3b8;
          text-align: center;
        }

        .step-item.done .step-label,
        .step-item.active .step-label {
          color: #334155;
          font-weight: 600;
        }

        /* Nav button */
        .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          border-radius: 10px;
          font-weight: 600;
          transition: all 0.3s ease;
          font-size: 0.9rem;
          border: none;
          cursor: pointer;
          text-decoration: none;
        }

        .btn-primary {
          background: linear-gradient(135deg, #008080, #006666);
          color: white;
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px -8px rgba(0,128,128,0.45);
        }

        .nav-consult-btn {
          padding: 0.6rem 1.2rem;
          font-size: 0.85rem;
        }

        /* Help note */
        .help-note {
          text-align: center;
          margin-top: 1.75rem;
          font-size: 0.875rem;
          color: #64748b;
        }

        .help-note a {
          color: #008080;
          font-weight: 600;
          text-decoration: none;
        }

        .help-note a:hover { text-decoration: underline; }

        /* Footer */
        .footer {
          background: #0f172a;
          color: #94a3b8;
          padding: 5rem 0 2rem;
          position: relative;
          overflow: hidden;
        }

        .footer-decoration {
          position: absolute;
          width: 300px;
          height: 300px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(14, 165, 233, 0.05) 0%, transparent 70%);
          pointer-events: none;
        }

        .footer-decoration:first-child {
          top: -100px;
          left: -100px;
        }

        .footer-decoration:last-child {
          bottom: -100px;
          right: -100px;
        }

        .footer-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 2.5rem;
          margin-bottom: 3rem;
        }

        .footer-brand {
          margin-bottom: 1rem;
        }

        .footer-brand .brand-logo-mark {
          width: 120px;
          height: auto;
        }

        .footer-about {
          color: #94a3b8;
          line-height: 1.6;
          font-size: 0.9rem;
          margin-bottom: 1.5rem;
        }

        .footer-social {
          display: flex;
          gap: 1rem;
        }

        .social-link {
          width: 36px;
          height: 36px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #94a3b8;
          transition: all 0.3s ease;
        }

        .social-link:hover {
          background: #0ea5e9;
          color: white;
          transform: translateY(-2px);
        }

        .footer-heading {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 1.25rem;
          color: white;
        }

        .footer-links {
          list-style: none;
          padding: 0;
        }

        .footer-links li {
          margin-bottom: 0.75rem;
        }

        .footer-links a {
          color: #94a3b8;
          text-decoration: none;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }

        .footer-links a:hover {
          color: #0ea5e9;
          transform: translateX(5px);
        }

        .footer-contact {
          margin-bottom: 1.5rem;
        }

        .contact-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.75rem;
          font-size: 0.9rem;
        }

        .contact-item a {
          color: #94a3b8;
          text-decoration: none;
        }

        .contact-item a:hover {
          color: #0ea5e9;
        }

        .footer-newsletter {
          margin-top: 1.5rem;
        }

        .newsletter-text {
          font-size: 0.85rem;
          margin-bottom: 0.75rem;
          color: #cbd5e1;
        }

        .newsletter-form {
          display: flex;
          gap: 0.5rem;
        }

        .newsletter-input {
          flex: 1;
          padding: 0.6rem 1rem;
          border: 1px solid #334155;
          background: #1e293b;
          border-radius: 8px;
          color: white;
          font-size: 0.85rem;
        }

        .newsletter-input:focus {
          outline: none;
          border-color: #0ea5e9;
        }

        .newsletter-btn {
          padding: 0.6rem 1rem;
          background: #0ea5e9;
          border: none;
          border-radius: 8px;
          color: white;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .newsletter-btn:hover {
          background: #0284c7;
        }

        .footer-bottom {
          padding-top: 2rem;
          border-top: 1px solid #334155;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
          font-size: 0.85rem;
        }

        .footer-legal {
          display: flex;
          gap: 1.5rem;
        }

        .footer-legal a {
          color: #94a3b8;
          text-decoration: none;
        }

        .footer-legal a:hover {
          color: #0ea5e9;
        }

        /* Spinner */
        .spin {
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .navbar-toggle {
            display: block;
          }

          .navbar-menu {
            position: fixed;
            top: 70px;
            left: 0;
            width: 100%;
            background: white;
            flex-direction: column;
            padding: 2rem;
            gap: 1.5rem;
            transform: translateY(-100%);
            opacity: 0;
            visibility: hidden;
            transition: all 0.3s ease;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
            z-index: 999;
          }

          .navbar-menu.active {
            transform: translateY(0);
            opacity: 1;
            visibility: visible;
          }

          .services-dropdown .dropdown-menu {
            position: static;
            opacity: 1;
            visibility: visible;
            transform: none;
            box-shadow: none;
            background: rgba(0, 128, 128, 0.05);
            margin-top: 1rem;
            border-radius: 8px;
          }
        }

        @media (max-width: 768px) {
          .page-hero {
            padding: 6rem 0 3rem;
          }

          .page-title {
            font-size: 2rem;
          }

          .hero-description {
            font-size: 1rem;
          }

          .lookup-card,
          .result-card {
            border-radius: 16px;
            padding: 1.5rem 1.25rem;
          }

          .result-card-top,
          .result-card-body {
            padding-left: 1.25rem;
            padding-right: 1.25rem;
          }

          .step-label {
            font-size: 0.58rem;
          }

          .footer-bottom {
            flex-direction: column;
            text-align: center;
          }

          .footer-legal {
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
}
